import React from 'react';
import styled from 'styled-components';

const PageHeader = styled.div`
  padding: 8rem 0 4rem;
  background-color: var(--color-primary-dark);
  color: var(--color-light);
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 50px;
    background: linear-gradient(to right bottom, var(--color-primary-dark) 49%, var(--color-white) 50%);
  }
`;

const PageHeaderContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const PageTitle = styled.h1`
  font-size: 3rem;
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const PageSubtitle = styled.p`
  font-size: 1.25rem;
  opacity: 0.9;
  max-width: 700px;
`;

const TechStackSection = styled.section`
  padding: 6rem 0;
`;

const SectionContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const SectionDescription = styled.p`
  font-size: 1.1rem;
  line-height: 1.7;
  margin-bottom: 3rem;
  max-width: 800px;
`;

const TechGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 2rem;
  margin-bottom: 4rem;
`;

const TechItem = styled.div`
  text-align: center;
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
  }
`;

const TechIcon = styled.div`
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
  
  i {
    font-size: 3rem;
    color: var(--color-accent);
  }
`;

const TechIconImage = styled.img`
  max-height: 60px;
  max-width: 60px;
`;

const TechName = styled.h3`
  font-size: 1.2rem;
  margin-bottom: 0.5rem;
`;

const TechDescription = styled.p`
  font-size: 0.9rem;
  color: var(--color-primary);
`;

const BlueprintSection = styled.section`
  padding: 6rem 0;
  background-color: var(--color-primary-dark);
  color: var(--color-light);
  position: relative;
  overflow: hidden;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: 
      linear-gradient(rgba(28, 43, 58, 0.85), rgba(28, 43, 58, 0.95)),
      url('/images/blueprint-bg.jpg');
    background-size: cover;
    background-position: center;
    z-index: 1;
  }
`;

const BlueprintContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  position: relative;
  z-index: 2;
`;

const BlueprintTitle = styled.h2`
  font-size: 2.5rem;
  margin-bottom: 2rem;
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const CodeSnippet = styled.div`
  background-color: #282c34;
  color: #abb2bf;
  padding: 1.5rem;
  border-radius: 8px;
  font-family: 'Consolas', 'Monaco', monospace;
  overflow-x: auto;
  margin-bottom: 2rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
`;

const CodeLine = styled.pre`
  margin: 0;
  padding: 0.25rem 0;
`;

const Comment = styled.span`
  color: #5c6370;
  font-style: italic;
`;

const Keyword = styled.span`
  color: #c678dd;
`;

const FunctionName = styled.span`
  color: #61afef;
`;

const String = styled.span`
  color: #98c379;
`;

const Number = styled.span`
  color: #d19a66;
`;

const ArchitectureSection = styled.section`
  padding: 6rem 0;
  background-color: var(--color-gray);
`;

const ArchitectureContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const ArchitectureTitle = styled.h2`
  font-size: 2.5rem;
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const ArchitectureDescription = styled.p`
  font-size: 1.1rem;
  line-height: 1.7;
  margin-bottom: 3rem;
  max-width: 800px;
`;

const DiagramContainer = styled.div`
  background-color: var(--color-white);
  border-radius: 8px;
  padding: 2rem;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  margin-bottom: 2rem;
`;

const DiagramImage = styled.div`
  width: 100%;
  height: 400px;
  background-image: url('/images/architecture-diagram.svg');
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
`;

const DiagramCaption = styled.p`
  text-align: center;
  margin-top: 1.5rem;
  font-style: italic;
  color: var(--color-primary);
`;

const TechStack = () => {
  return (
    <>
      <PageHeader>
        <PageHeaderContent>
          <PageTitle>Tech Stack Expertise</PageTitle>
          <PageSubtitle>
            We specialize in a range of technologies to build resilient, scalable systems that meet your unique needs
          </PageSubtitle>
        </PageHeaderContent>
      </PageHeader>
      
      <TechStackSection>
        <SectionContainer>
          <SectionTitle>Cloud Platforms</SectionTitle>
          <SectionDescription>
            We design and implement cloud-native solutions that leverage the best features of modern cloud platforms to deliver scalable, resilient systems.
          </SectionDescription>
          
          <TechGrid>
            <TechItem>
              <TechIcon>
                <i className="fab fa-aws"></i>
              </TechIcon>
              <TechName>AWS</TechName>
              <TechDescription>
                Amazon Web Services infrastructure and services
              </TechDescription>
            </TechItem>
            
            <TechItem>
              <TechIcon>
                <i className="fab fa-microsoft"></i>
              </TechIcon>
              <TechName>Azure</TechName>
              <TechDescription>
                Microsoft Azure cloud platform and services
              </TechDescription>
            </TechItem>
            
            <TechItem>
              <TechIcon>
                <i className="fab fa-google"></i>
              </TechIcon>
              <TechName>Google Cloud</TechName>
              <TechDescription>
                Google Cloud Platform infrastructure and services
              </TechDescription>
            </TechItem>
            
            <TechItem>
              <TechIcon>
                <i className="fas fa-server"></i>
              </TechIcon>
              <TechName>Hybrid Cloud</TechName>
              <TechDescription>
                Mixed on-premises and cloud environments
              </TechDescription>
            </TechItem>
            
            <TechItem>
              <TechIcon>
                <i className="fas fa-network-wired"></i>
              </TechIcon>
              <TechName>Multi-Cloud</TechName>
              <TechDescription>
                Cross-platform cloud architectures
              </TechDescription>
            </TechItem>
          </TechGrid>
          
          <SectionTitle>Infrastructure & DevOps</SectionTitle>
          <SectionDescription>
            We implement modern DevOps practices and infrastructure automation to ensure reliability, security, and efficiency in your technology operations.
          </SectionDescription>
          
          <TechGrid>
            <TechItem>
              <TechIcon>
                <i className="fab fa-docker"></i>
              </TechIcon>
              <TechName>Docker</TechName>
              <TechDescription>
                Containerization and microservices
              </TechDescription>
            </TechItem>
            
            <TechItem>
              <TechIcon>
                <i className="fas fa-dharmachakra"></i>
              </TechIcon>
              <TechName>Kubernetes</TechName>
              <TechDescription>
                Container orchestration and management
              </TechDescription>
            </TechItem>
            
            <TechItem>
              <TechIcon>
                <i className="fas fa-code-branch"></i>
              </TechIcon>
              <TechName>Terraform</TechName>
              <TechDescription>
                Infrastructure as code automation
              </TechDescription>
            </TechItem>
            
            <TechItem>
              <TechIcon>
                <i className="fas fa-sync-alt"></i>
              </TechIcon>
              <TechName>CI/CD</TechName>
              <TechDescription>
                Automated deployment pipelines
              </TechDescription>
            </TechItem>
            
            <TechItem>
              <TechIcon>
                <i className="fas fa-chart-line"></i>
              </TechIcon>
              <TechName>Monitoring</TechName>
              <TechDescription>
                System observability and alerting
              </TechDescription>
            </TechItem>
            
            <TechItem>
              <TechIcon>
                <i className="fas fa-shield-alt"></i>
              </TechIcon>
              <TechName>Security</TechName>
              <TechDescription>
                DevSecOps and security automation
              </TechDescription>
            </TechItem>
          </TechGrid>
          
          <SectionTitle>Development & Data</SectionTitle>
          <SectionDescription>
            We leverage modern development practices and data technologies to build robust, scalable applications that deliver business value.
          </SectionDescription>
          
          <TechGrid>
            <TechItem>
              <TechIcon>
                <i className="fas fa-database"></i>
              </TechIcon>
              <TechName>SQL & NoSQL</TechName>
              <TechDescription>
                Relational and document databases
              </TechDescription>
            </TechItem>
            
            <TechItem>
              <TechIcon>
                <i className="fab fa-node-js"></i>
              </TechIcon>
              <TechName>Node.js</TechName>
              <TechDescription>
                JavaScript runtime environment
              </TechDescription>
            </TechItem>
            
            <TechItem>
              <TechIcon>
                <i className="fab fa-java"></i>
              </TechIcon>
              <TechName>Java</TechName>
              <TechDescription>
                Enterprise application development
              </TechDescription>
            </TechItem>
            
            <TechItem>
              <TechIcon>
                <i className="fab fa-python"></i>
              </TechIcon>
              <TechName>Python</TechName>
              <TechDescription>
                Data processing and automation
              </TechDescription>
            </TechItem>
            
            <TechItem>
              <TechIcon>
                <i className="fas fa-exchange-alt"></i>
              </TechIcon>
              <TechName>APIs</TechName>
              <TechDescription>
                RESTful and GraphQL interfaces
              </TechDescription>
            </TechItem>
            
            <TechItem>
              <TechIcon>
                <i className="fas fa-stream"></i>
              </TechIcon>
              <TechName>Kafka</TechName>
              <TechDescription>
                Event streaming and processing
              </TechDescription>
            </TechItem>
          </TechGrid>
        </SectionContainer>
      </TechStackSection>
      
      <BlueprintSection>
        <BlueprintContainer>
          <BlueprintTitle>Architecture Design Principles</BlueprintTitle>
          <CodeSnippet>
            <CodeLine><Comment>// Core architecture principles implemented across our projects</Comment></CodeLine>
            <CodeLine><Keyword>class</Keyword> <FunctionName>KriegerlabsArchitecture</FunctionName> {'{'}</CodeLine>
            <CodeLine>  <FunctionName>constructor</FunctionName>() {'{'}</CodeLine>
            <CodeLine>    <Keyword>this</Keyword>.principles = {'{'}  <Comment>// Our foundational principles</Comment></CodeLine>
            <CodeLine>      scalability: <String>"Design for 10x current load"</String>,</CodeLine>
            <CodeLine>      resilience: <String>"Embrace failure as normal and design accordingly"</String>,</CodeLine>
            <CodeLine>      security: <String>"Security by design, not afterthought"</String>,</CodeLine>
            <CodeLine>      modularity: <String>"Loosely coupled components with clear boundaries"</String>,</CodeLine>
            <CodeLine>      observability: <String>"If you can't measure it, you can't improve it"</String></CodeLine>
            <CodeLine>    {'}'}</CodeLine>
            <CodeLine>    </CodeLine>
            <CodeLine>    <Keyword>this</Keyword>.patterns = [  <Comment>// Patterns we frequently leverage</Comment></CodeLine>
            <CodeLine>      <String>"Event-driven architecture"</String>,</CodeLine>
            <CodeLine>      <String>"Microservices when justified"</String>,</CodeLine>
            <CodeLine>      <String>"Command Query Responsibility Segregation (CQRS)"</String>,</CodeLine>
            <CodeLine>      <String>"Domain-Driven Design"</String>,</CodeLine>
            <CodeLine>      <String>"Infrastructure as Code"</String></CodeLine>
            <CodeLine>    ]</CodeLine>
            <CodeLine>  {'}'}</CodeLine>
            <CodeLine>  </CodeLine>
            <CodeLine>  <FunctionName>evaluateSolution</FunctionName>(requirements) {'{'}</CodeLine>
            <CodeLine>    <Keyword>const</Keyword> solutionOptions = <Keyword>this</Keyword>.<FunctionName>analyzeTradeoffs</FunctionName>(requirements);</CodeLine>
            <CodeLine>    <Keyword>return</Keyword> solutionOptions.<FunctionName>filter</FunctionName>(option => </CodeLine>
            <CodeLine>      option.<FunctionName>meetsScalabilityRequirements</FunctionName>() && </CodeLine>
            <CodeLine>      option.<FunctionName>maintainsResilience</FunctionName>() && </CodeLine>
            <CodeLine>      option.<FunctionName>ensuresSecurity</FunctionName>() && </CodeLine>
            <CodeLine>      option.<FunctionName>alignsWithBusinessGoals</FunctionName>()</CodeLine>
            <CodeLine>    );</CodeLine>
            <CodeLine>  {'}'}</CodeLine>
            <CodeLine>{'}'}</CodeLine>
          </CodeSnippet>
        </BlueprintContainer>
      </BlueprintSection>
      
      <ArchitectureSection>
        <ArchitectureContainer>
          <ArchitectureTitle>Reference Architecture</ArchitectureTitle>
          <ArchitectureDescription>
            We develop tailored architectural blueprints for each client, but our reference architectures provide a starting point that incorporates our best practices and patterns.
          </ArchitectureDescription>
          
          <DiagramContainer>
            <DiagramImage />
            <DiagramCaption>
              Sample microservices architecture with event-driven communication and multi-tier security
            </DiagramCaption>
          </DiagramContainer>
        </ArchitectureContainer>
      </ArchitectureSection>
    </>
  );
};

export default TechStack;